/** Inert source text: validated UTF-8 bytes only, never an executable/HTML/Markdown document. */
export const SOURCE_TEXT_MAX_BYTES = 1024 * 1024;
export const SOURCE_TEXT_EXTENSIONS = [
  'txt',
  'log',
  'csv',
  'md',
  'markdown',
  'html',
  'htm',
] as const;
export function isSourceTextFilename(filename: string): boolean {
  return (
    filename.length > 0 &&
    filename.length <= 256 &&
    !/[\\/]|\.\./.test(filename) &&
    !Array.from(filename).some(
      (character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
    ) &&
    SOURCE_TEXT_EXTENSIONS.some((ext) => filename.toLowerCase().endsWith(`.${ext}`))
  );
}
export function normalizeSourceTextUpload(
  filename: string,
  mime: string,
): { contentType: 'text/plain'; filename: string } | null {
  if (!isSourceTextFilename(filename)) return null;
  const type = mime.toLowerCase().trim();
  const extension = filename.toLowerCase().split('.').at(-1);
  const common = ['', 'text/plain', 'application/octet-stream'];
  if (
    !common.includes(type) &&
    !(extension === 'csv' && ['text/csv', 'application/csv'].includes(type)) &&
    !(['md', 'markdown'].includes(extension ?? '') && type === 'text/markdown') &&
    !(['html', 'htm'].includes(extension ?? '') && type === 'text/html')
  )
    return null;
  return { contentType: 'text/plain', filename };
}
/** Fatal decoding; canonical LF line endings, no BOM or binary/control characters. */
export function canonicalizeSourceText(bytes: Uint8Array): Uint8Array {
  if (bytes.length === 0 || bytes.length > SOURCE_TEXT_MAX_BYTES)
    throw Error('INVALID_SOURCE_TEXT');
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(bytes);
  } catch {
    throw Error('INVALID_SOURCE_TEXT');
  }
  if (text.startsWith('\uFEFF')) text = text.slice(1);
  for (const character of text) {
    const code = character.codePointAt(0) ?? 0;
    if ((code < 32 && code !== 9 && code !== 10 && code !== 13) || (code >= 127 && code <= 159))
      throw Error('INVALID_SOURCE_TEXT');
  }
  text = text.replace(/\r\n?/g, '\n');
  const result = new TextEncoder().encode(text);
  if (!result.length || result.length > SOURCE_TEXT_MAX_BYTES) throw Error('INVALID_SOURCE_TEXT');
  return result;
}
export function isCanonicalSourceText(bytes: Uint8Array): boolean {
  try {
    const canonical = canonicalizeSourceText(bytes);
    return (
      canonical.length === bytes.length && canonical.every((value, index) => value === bytes[index])
    );
  } catch {
    return false;
  }
}
export function sourceTextStoredFilename(filename: string): string {
  if (!isSourceTextFilename(filename)) throw Error('INVALID_SOURCE_TEXT_FILENAME');
  const dot = filename.lastIndexOf('.');
  const base = filename
    .slice(0, dot)
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .slice(0, 220);
  const ext = filename.slice(dot + 1).toLowerCase();
  return ext === 'txt' ? `${base}.txt` : `${base}.${ext}.txt`;
}
